import {
  resetCall,
  setIsCalling,
  setIsIncoming,
  setIsOutgoing,
  setMuted,
  setOtherUserId,
  setRecordLoading,
  setRecording,
  setScreenShared,
  setVideoOff,
  setConversationId,
  setOtherUserVideoOff,
  setOtherUserScreenShared
} from '../../store/call';
import store from '../../store';
import { socket } from '../hooks';
import { toast } from 'react-toastify';
import { createRecording } from '../api/message';
import MultiStreamsMixer from 'multistreamsmixer';
import Transcriber from '../transcriber/Transcriber';
import callignSound from '../../assets/audio/calling.mp3';

export default class PeerCall {
  static connected = false;
  static getCalled = false;
  static expectScreenShare = false;
  static mixer: MultiStreamsMixer = null;
  static recorder: MediaRecorder | null = null;
  static recordingData: { id: string; filename: string } | null = null;
  static localTranscriber: Transcriber | null = null;
  static peerConnection;
  static data;
  static sound = new Audio();
  static medias: Medias = {
    remoteMedia: undefined,
    localMedia: undefined
  };
  static iceCandidates: IceCandidates = { localICECandidates: [] };
  static streams: Streams = {
    localStream: undefined,
    remoteStream: undefined,
    screenStream: undefined
  };
  static tracks: Tracks = { localVideoTrack: undefined };

  static dispatch = store.dispatch;

  static get state() {
    return store.getState();
  }

  static get callState() {
    return this.state?.call;
  }

  static get otherUserId() {
    return this.callState?.otherUserId;
  }

  static set otherUserId(val) {
    this.dispatch(setOtherUserId(val));
  }

  static get conversationId() {
    return this.callState?.conversationId;
  }

  static set conversationId(val) {
    this.dispatch(setConversationId(val));
  }

  static get userId() {
    const user = this.state?.auth?.user?.user;
    return user?.id || user;
  }

  static get recording() {
    return this.callState?.recording;
  }

  static set recording(v) {
    this.dispatch(setRecording(v));
  }

  static get isCalling() {
    return this.callState?.isCalling;
  }

  static set isCalling(val) {
    this.dispatch(setIsCalling(val));
  }

  static get isIncoming() {
    return this.callState?.isIncoming;
  }

  static set isIncoming(val) {
    this.dispatch(setIsIncoming(val));
  }

  static get isOutgoing() {
    return this.callState?.isOutgoing;
  }

  static set isOutgoing(val) {
    this.dispatch(setIsOutgoing(val));
  }

  static get muted() {
    return this.callState?.muted;
  }

  static set muted(val) {
    this.dispatch(setMuted(val));
  }

  static get screenShared() {
    return this.callState?.screenShared;
  }

  static set screenShared(val) {
    this.dispatch(setScreenShared(val));
    this.emitCallState({ screenShared: val });
  }

  static get videoOff() {
    return this.callState?.videoOff;
  }

  static set videoOff(val) {
    this.dispatch(setVideoOff(val));
  }

  static get recordLoading() {
    return this.callState?.recordLoading;
  }

  static set recordLoading(val) {
    this.dispatch(setRecordLoading(val));
  }

  static get remoteMedia() {
    return this.medias?.remoteMedia;
  }

  static get localMedia() {
    return this.medias?.localMedia;
  }

  static get isOnCal() {
    return this.isCalling && this.streams?.remoteStream;
  }

  static set remoteMedia(value) {
    this.medias.remoteMedia = value;
  }

  static set localMedia(value) {
    this.medias.localMedia = value;
  }

  static set otherUserVideoOff(val) {
    this.dispatch(setOtherUserVideoOff(val));
  }

  static set otherUserScreenShared(val) {
    this.dispatch(setOtherUserScreenShared(val));
  }

  static get otherUserVideoOff() {
    return this.callState?.otherUser?.videoOff;
  }

  static get otherUserScreenShared() {
    return this.callState?.otherUser?.screenShared;
  }

  static createLocalMedia = (media) => (this.medias.localMedia = media);

  static createRemoteMedia = (media) => (this.medias.remoteMedia = media);

  static addStreamToLocalMediaSource = (stream) => {
    if (this.localMedia) {
      // @ts-expect-error
      this.medias.localMedia.srcObject = stream;
      // @ts-expect-error
      this.medias.localMedia.volume = 0;
    }
  };

  static setRemoteMediaSource = (stream) => {
    if (this.remoteMedia) {
      // @ts-expect-error
      this.medias.remoteMedia.srcObject = stream;
    }
  };

  static resetRemoteMediaSource = () =>
    this.remoteMedia && this.setRemoteMediaSource(this.streams.remoteStream);

  static callUser = async (id, conversationId) => {
    this.otherUserId = id;
    this.conversationId = conversationId;
  };

  static startCall = async (media, conversation_id) => {
    this.createLocalMedia(media);
    await this.initMedia();

    if (this.isOnCal) {
      return;
    }

    // get iceServers
    if (this.getCalled) {
      if (this.data?.response.iceServers) {
        await this.initPeerConnection(this.data?.response);
      }

      await this.sendAnswer();
    } else {
      socket.emit('call-data', undefined, async (exchangeData) => {
        this.initPeerConnection(exchangeData);
        this.sendOffer(exchangeData, conversation_id);
      });
    }
  };

  static initMedia = async () => {
    const stream = await this.getLocalStream();

    if (this.screenShared && this.streams.screenStream) {
      this.addStreamToLocalMediaSource(this.streams.screenStream);
    } else {
      this.addStreamToLocalMediaSource(stream);
    }

    return stream;
  };

  static getLocalStream = async () => {
    if (this.streams?.localStream) return this.streams.localStream;

    const stream = await this.getMediaStream();
    this.streams.localStream = stream;

    return stream;
  };

  static getMediaStream = () =>
    navigator.mediaDevices.getUserMedia({
      video: true,
      audio: true
    });

  static endCall = () => {
    // Check if we have an active connection before trying to close it
    if (this.peerConnection) {
      try {
        this.peerConnection.close();
        this.peerConnection.onicecandidate = null;
        this.peerConnection.ontrack = null;
        this.peerConnection.onaddstream = null;
        this.peerConnection.onconnectionstatechange = null;
      } catch (error) {
        console.error("Error closing peer connection:", error);
      }
    }
    
    // Exit picture-in-picture mode if active
    if (document.pictureInPictureElement) {
      document.exitPictureInPicture().catch(err => 
        console.error("Error exiting picture-in-picture:", err)
      );
    }

    this.stopSound();
    
    // Stop recording if active
    if (this.recording) {
      this.stopRecording();
    }

    this.stopLocalMediaTracks();
    this.clearLocalStream();

    // Only emit end-call event if we have necessary IDs
    if (this.data?.from || this.otherUserId) {
      socket.emit('end-call', {
        to: this.data?.from || this.otherUserId,
        from: this.userId,
        conversation_id: this.data?.conversation_id || this.conversationId
      });
    }

    // Reset all state
    this.otherUserId = undefined;
    this.conversationId = undefined;
    this.isIncoming = false;
    this.isOutgoing = false;
    this.isCalling = false;

    this.data = undefined;
    this.streams.remoteStream = undefined;
    this.iceCandidates.localICECandidates = [];
    this.tracks.localVideoTrack = undefined;
    this.connected = false;
    this.getCalled = false;

    this.resetState();
  };

  static sendOffer = async (response, conversation_id) => {
    const offer = await this.peerConnection?.createOffer({
      offerToReceiveVideo: true
    });

    await this.peerConnection?.setLocalDescription(offer);

    socket.emit('outgoing-call', {
      response,
      offer,
      to: this.otherUserId,
      from: this.userId,
      conversation_id
    });
  };

  static initPeerConnection = ({ iceServers }: { iceServers: RTCIceServer[] }) => {
    try {
      this.peerConnection = new RTCPeerConnection({
        iceServers
        // offerToReceiveAudio: true,
        // offerToReceiveVideo: true
      });
    } catch (error) {
      console.error(error);
      alert('Error initializing connection');
      throw error;
    }

    if (!this.peerConnection) throw Error('Peer Connection not set');

    this.peerConnection.onicecandidate = (event) => {
      if (event.candidate) {
        if (this.connected) {
          socket.emit('candidate', {
            candidate: event.candidate,
            to: this.otherUserId,
            from: this.userId
          });
        } else {
          this.iceCandidates.localICECandidates.push(event.candidate);
        }
      }
    };

    if (this.streams.localStream)
      this.streams.localStream
        ?.getTracks()
        .map((track) => this.peerConnection?.addTrack(track, this.streams.localStream));

    this.peerConnection.onconnectionstatechange = async () => {
      const connection = this.peerConnection;
      switch (connection?.connectionState) {
        case 'connected':
          this.connected = true;
          this.tracks.localVideoTrack = this.streams.localStream?.getVideoTracks()[0];

          if (this.tracks.localVideoTrack) {
            this.streams.localStream?.addTrack(this.tracks.localVideoTrack);

            const sender = this.peerConnection
              ?.getSenders()
              .find((s) => s.track?.kind === this.tracks.localVideoTrack?.kind);

            sender?.replaceTrack(this.tracks.localVideoTrack);
          }

          this.iceCandidates.localICECandidates.forEach((candidate) => {
            socket.emit('candidate', {
              candidate,
              to: this.otherUserId,
              from: this.userId
            });
          });
          this.iceCandidates.localICECandidates = [];
          break;
        case 'failed':
          const offer = await connection.createOffer({
            iceRestart: true,
            offerToReceiveVideo: true
          });

          connection.setLocalDescription(new RTCSessionDescription(offer));
          break;
        default:
          break;
      }
    };

    this.peerConnection.onaddstream = ({ stream }) => {
      if (this.remoteMedia && stream && this.streams.remoteStream !== stream) {
        this.streams.remoteStream = stream;
        this.setRemoteMediaSource(stream);
      }
    };

    this.peerConnection.ontrack = ({ streams: [stream], track }) => {
      if (this.expectScreenShare) {
        this.streams.remoteStream?.addTrack(track);
        this.expectScreenShare = false;
        return;
      }

      this.setRemoteMediaSource(stream);
    };
  };

  static playSound = (src, callOn) => {
    this.sound.src = src;
    callOn && callOn(this.sound);
    this.sound.play();
  };

  static pauseSound = () => this.sound.pause();

  // @ts-expect-error
  static resumeSound = () => this.sound.resume();

  static stopSound = () => {
    this.sound.pause();
    this.sound.srcObject = null;
  };

  static mute = () => {
    const muted = store.getState()?.call?.muted;
    this.streams.localStream?.getAudioTracks().forEach((track) => (track.enabled = !!muted));

    if (this.localMedia) {
      // this.remoteMedia && (this.remoteMedia.muted = !muted);
      this.muted = !muted;
    }
  };

  static resetState = () => store.dispatch(resetCall());

  static fullScreen = () => this.remoteMedia?.requestFullscreen();

  static setBandwidth = async () => {
    if ('RTCRtpSender' in window && 'setParameters' in window.RTCRtpSender.prototype) {
      const sender = this.peerConnection.getSenders()[0];
      const parameters = sender.getParameters();
      if (!parameters.encodings) {
        parameters.encodings = [{}];
      }
      parameters.encodings[0].maxBitrate = 200 * 1000;

      try {
        await sender.setParameters(parameters);
      } catch (error) {
        console.error(error);
      }
    }
  };

  static startScreenSharing = async () => {
    try {
      this.streams.screenStream = await navigator.mediaDevices.getDisplayMedia({
        // @ts-expect-error
        video: { cursor: 'always' },
        audio: false
      });
    } catch (e) {
      toast.error('Could not share the screen, please check the error and try again. ' + e);
    }

    if (this.streams.screenStream) {
      this.screenShared = true;

      const screenVideoTrack = this.streams.screenStream.getVideoTracks()[0];

      const sender = this.peerConnection
        .getSenders()
        .find((s) => s.track?.kind === screenVideoTrack.kind);
      if (sender) {
        this.tracks.localVideoTrack = this.streams.localStream?.getVideoTracks()[0];

        this.addStreamToLocalMediaSource(this.streams.screenStream);

        sender.replaceTrack(screenVideoTrack);

        this.recording &&
          this.mixer?.resetVideoStreams([this.streams.screenStream, this.streams.remoteStream]);
      } else {
        console.error('Track sender not found');
      }

      screenVideoTrack.addEventListener('ended', () => this.stopScreenSharing());
    }
  };

  static stopScreenSharing = () => {
    this.streams.screenStream?.getVideoTracks()?.forEach((track) => track.stop());

    if (this.tracks.localVideoTrack) {
      this.streams.localStream?.addTrack(this.tracks.localVideoTrack);

      const sender = this.peerConnection
        .getSenders()
        .find((s) => s.track?.kind === this.tracks.localVideoTrack?.kind);

      this.addStreamToLocalMediaSource(this.streams.localStream);

      sender.replaceTrack(this.tracks.localVideoTrack);

      this.recording &&
        this.mixer?.resetVideoStreams([this.streams.localStream, this.streams.remoteStream]);
    } else {
      socket.emit('expectScreenShare', {
        removeTrack: true,
        to: this.data.from,
        from: this.data.to
      });
    }

    this.screenShared = false;
  };

  static emitCallState = (state?: {}) =>
    socket.emit('video-state', {
      videoOff: this.videoOff,
      screenShared: this.screenShared,
      to: this.data.from,
      from: this.data.to,
      ...state
    });

  static toggleVideo = () => {
    const videoOff = this.videoOff;
    this.videoOff = !videoOff;
    this.emitCallState();
    this.streams.localStream?.getVideoTracks().forEach((track) => (track.enabled = videoOff));
    toast.success(videoOff ? 'Camera has been turned on.' : 'Camera has been turned off.');
  };

  static clearLocalStream = () => {
    this.streams.localStream?.getTracks().forEach((track) => track.stop());
    this.streams.localStream = undefined;
  };

  static clearMediaSource = () => {
    this.addStreamToLocalMediaSource(undefined);

    this.setRemoteMediaSource(undefined);

    this.medias.localMedia = undefined;
    this.medias.remoteMedia = undefined;
  };

  static cleanup = () => this.endCall();

  static getLocalMediaSource = () =>
    this.medias?.localMedia?.srcObject || this.medias?.localMedia?.srcObject;

  static stopLocalMediaTracks = () => {
    const source = this.getLocalMediaSource();

    if (source) {
      // @ts-expect-error
      const tracks = source.getTracks();
      tracks.forEach((track) => track.stop());
      this.clearMediaSource();
    }
  };

  static answerIncoming = async (isIncoming = false) => {
    this.stopSound();
    const data = this.data;

    if (!data) throw Error('Data not initialized');

    this.connected = true;
    this.getCalled = true;

    if (isIncoming) {
      this.otherUserId = data.to;
      this.isIncoming = false;
      this.isCalling = true;
    } else {
      this.isCalling = true;
    }
  };

  static startRecording = async () => {
    this.recordLoading = true;
    const { data: recording } = await createRecording({ conversation: this.data?.conversation_id });
    this.mixer = new MultiStreamsMixer([this.streams.localStream, this.streams.remoteStream]);
    this.localTranscriber = new Transcriber(this.streams.localStream);

    this.mixer.frameInterval = 1;
    this.mixer.startDrawingFrames();

    this.recorder = new MediaRecorder(this.mixer.getMixedStream(), {
      mimeType: 'video/webm;codecs=vp8,opus'
    });

    const recorder = this.recorder;
    this.recordingData = recording;

    recorder.start(1000);
    this.localTranscriber?.start();

    socket.emit('Recording.start', {
      conversation: { id: this.data?.conversation_id },
      to: this.data?.from || this.otherUserId
    });

    this.recording = true;
    this.recordLoading = false;

    recorder.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) {
        socket.emit('Recording.chunk', {
          data: e.data,
          conversation: { id: this.data?.conversation_id },
          recording: { filename: recording.filename, id: recording.id }
        });
      }
    };
    toast.info('This call is being recorded', { autoClose: 3000 });
  };

  static stopRecording = () => {
    if (this.recording) {
      this.mixer.releaseStreams();
      this.recorder?.stop();
      this.localTranscriber?.stop();

      socket.emit('Recording.end', {
        conversation: { id: this.data?.conversation_id },
        to: this.data?.from || this.otherUserId,
        from: this.userId,
        recording: { id: this.recordingData?.id, filename: this.recordingData?.filename },
        transcripts: this.localTranscriber.transcripts
      });
      // will remove later
      this.recording = false;
    }
  };

  static sendAnswer = async () => {
    const data = this.data;

    if (!data) throw Error('Data not initialized');

    await this.peerConnection?.setRemoteDescription(new RTCSessionDescription(data.offer));
    const answer = await this.peerConnection?.createAnswer();
    await this.peerConnection?.setLocalDescription(answer);

    socket.emit('answer-incoming-call', {
      answer,
      to: data.from,
      from: data.to
    });
  };

  static rejectIncoming = () => {
    socket.emit('reject-incoming', {
      to: this.data?.from,
      from: this.userId
    });
    this.cleanup();
  };

  static onIncoming = async (data) => {
    this.data = data;

    if (!this.getCalled) {
      this.playSound(callignSound, (sound) => (sound.loop = true));
      this.isIncoming = true;
      return;
    }

    this.answerIncoming();
  };

  static onExpectScreenShare = (data) => (this.expectScreenShare = data.value);

  static onVideoState = (data) => {
    this.otherUserVideoOff = data.videoOff;
    this.otherUserScreenShared = data.screenShared;
  };

  static onAnswered = async (data) => {
    await this.peerConnection?.setRemoteDescription(new RTCSessionDescription(data.answer));

    this.data = data;
    this.connected = true;

    this.iceCandidates.localICECandidates.forEach((candidate) => {
      socket.emit('candidate', {
        candidate,
        to: this.data?.from,
        from: this.userId
      });
    });
    this.iceCandidates.localICECandidates = [];

    if (!this.isCalling) {
      this.setBandwidth();
      this.isIncoming = false;
      this.isCalling = true;
    } else {
      this.isOutgoing = false;
    }
    store.getState()?.app.company.options.autoRecord && setTimeout(this.startRecording, 1000);
  };

  static onReject = () => this.cleanup();

  static onEnded = () => this.cleanup();

  static onCandidate = ({ candidate }) =>
    candidate && this.peerConnection?.addIceCandidate(new RTCIceCandidate(candidate));

  static listeners = {
    'incoming-call': this.onIncoming,
    'outgoing-answered': this.onAnswered,
    'outgoing-rejected': this.onReject,
    'call-ended': this.onEnded,
    expectScreenShare: this.onExpectScreenShare,
    candidate: this.onCandidate,
    'video-state': this.onVideoState
  };
}

interface Medias {
  [key: string]: HTMLVideoElement | undefined;
}

interface IceCandidates {
  [key: string]: any;
}

interface Streams {
  [key: string]: MediaStream | undefined;
}

interface Tracks {
  [key: string]: MediaStreamTrack | undefined;
}